import axiosInstance from './apiinterseptor';

export enum NotificationType {
  REGULAR = 'REGULAR',
  ADMIN_POPUP = 'ADMIN_POPUP'
}

export interface Notification {
  _id: string;
  title: string;
  message: string;
  createdBy: {
    _id: string;
    username: string;
    email?: string;
  };
  targetUsers: string[];
  type: NotificationType;
  readBy: string[];
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationPaginatedResponse {
  notifications: Notification[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  unreadCount: number;
}

export interface CreateNotificationRequest {
  title: string;
  message: string;
  type?: NotificationType;
  targetRoles?: string[];
}

/**
 * Get notifications for the current user
 */
export const getNotifications = async (options: {
  page?: number;
  limit?: number;
  unreadOnly?: boolean;
} = {}): Promise<NotificationPaginatedResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (options.page) queryParams.append('page', options.page.toString());
    if (options.limit) queryParams.append('limit', options.limit.toString());
    if (options.unreadOnly) queryParams.append('unreadOnly', 'true');

    const response = await axiosInstance.get(`/notifications?${queryParams.toString()}`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching notifications:', error.response?.data?.message);
    throw error;
  }
};

/**
 * Create a new notification
 */
export const createNotification = async (data: CreateNotificationRequest): Promise<Notification> => {
  try {
    const response = await axiosInstance.post('/notifications', data);
    return response.data.data;
  } catch (error: any) {
    console.error('Error creating notification:', error.response?.data?.message);
    throw error;
  }
};

/**
 * Mark a notification as read
 */
export const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  try {
    await axiosInstance.patch(`/notifications/${notificationId}/read`);
  } catch (error: any) {
    console.error('Error marking notification as read:', error.response?.data?.message);
    throw error;
  }
};

/**
 * Mark all notifications as read
 */
export const markAllNotificationsAsRead = async (): Promise<void> => {
  try {
    await axiosInstance.patch('/notifications/read-all');
  } catch (error: any) {
    console.error('Error marking all notifications as read:', error.response?.data?.message);
    throw error;
  }
};

/**
 * Delete a notification
 */
export const deleteNotification = async (notificationId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/notifications/${notificationId}`);
  } catch (error: any) {
    console.error('Error deleting notification:', error.response?.data?.message);
    throw error;
  }
};

/**
 * Get unread admin popup notifications
 */
export const getUnreadAdminPopupNotifications = async (): Promise<Notification[]> => {
  try {
    const response = await axiosInstance.get('/notifications/admin-popups');
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching admin popup notifications:', error.response?.data?.message);
    throw error;
  }
};

